/**
 * Just enough of a zip reader to unpack a `.webapp.zip` in the browser (`docs/board-api.md`, *The
 * `.webapp.zip` bundle format*): the central directory, stored and deflated entries, and nothing
 * else. `web-pack` writes small, flat, ordinary archives, so ZIP64, encryption and multi-disk
 * archives are refused rather than half-read.
 *
 * Inflating is the browser's own `DecompressionStream('deflate-raw')`, so there is no library here.
 */

export interface ZipEntry {
  /** The path inside the archive, `/`-separated, never absolute and never climbing out. */
  path: string;
  bytes: Uint8Array;
}

const EOCD = 0x06054b50;
const CENTRAL = 0x02014b50;
const LOCAL = 0x04034b50;

export async function unzip(data: Uint8Array): Promise<ZipEntry[]> {
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  const end = findEnd(view);
  const count = view.getUint16(end + 10, true);
  let at = view.getUint32(end + 16, true);
  if (count === 0xffff || at === 0xffffffff) throw new Error('This archive is ZIP64, which a web build never is.');

  const names = new TextDecoder();
  const entries: ZipEntry[] = [];
  for (let index = 0; index < count; index += 1) {
    if (view.getUint32(at, true) !== CENTRAL) throw new Error('The archive’s directory is damaged.');
    const flags = view.getUint16(at + 8, true);
    const method = view.getUint16(at + 10, true);
    const compressed = view.getUint32(at + 20, true);
    const nameLength = view.getUint16(at + 28, true);
    const extraLength = view.getUint16(at + 30, true);
    const commentLength = view.getUint16(at + 32, true);
    const local = view.getUint32(at + 42, true);
    const path = names.decode(data.subarray(at + 46, at + 46 + nameLength));
    at += 46 + nameLength + extraLength + commentLength;

    if (path.endsWith('/')) continue;
    if (flags & 1) throw new Error(`${path} is encrypted.`);
    if (path.startsWith('/') || path.split('/').some((part) => part === '..' || part === '')) {
      throw new Error(`${path} is not a path inside the build.`);
    }
    if (view.getUint32(local, true) !== LOCAL) throw new Error(`${path} is damaged.`);
    const start = local + 30 + view.getUint16(local + 26, true) + view.getUint16(local + 28, true);
    const raw = data.subarray(start, start + compressed);
    entries.push({ path, bytes: await expand(raw, method, path) });
  }
  return entries;
}

function findEnd(view: DataView): number {
  // The end record is 22 bytes plus a comment of at most 65 535.
  for (let at = view.byteLength - 22; at >= Math.max(0, view.byteLength - 22 - 0xffff); at -= 1) {
    if (view.getUint32(at, true) === EOCD) return at;
  }
  throw new Error('This is not a zip archive.');
}

async function expand(raw: Uint8Array, method: number, path: string): Promise<Uint8Array> {
  if (method === 0) return raw;
  if (method !== 8) throw new Error(`${path} uses a compression this preview does not read.`);
  const stream = new Blob([raw as BlobPart]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}
